<template>
  <v-dialog
    v-model="isVisible"
    fullscreen
    hide-overlay
    no-click-animation
    persistent
    transition="dialog-bottom-transition"
  >
    <v-sheet class="fill-height">
      <div class="fill-height pa-4 d-flex flex-column">
        <div class="mx-4 mt-4 mb-4">
          <div class="d-flex align-center">
            <div class="mb-2 text-h5 primary--text w-50">
              Quick Interview
            </div>
            <div class="w-50">
              <div
                v-if="!isLoading && totalStepCount"
                class="text-body-2 text-right"
              >
                Step {{ currentStepNumber }} of {{ totalStepCount }}

                <v-menu
                  :disabled="isSaving"
                  max-height="80vh"
                  offset-y
                  left
                >
                  <template #activator="{ attrs, on }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon>mdi-menu-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item-group
                      v-model="currentStepIndex"
                    >
                      <v-list-item
                        v-for="(item, index) of stepList"
                        :key="`step_list_${index}`"
                        :disabled="!item.isReviewed && (index + 1) !== unlockedStepCount"
                        :value="index"
                      >
                        <v-list-item-avatar
                          class="text-caption"
                          color="grey lighten-3"
                          size="24"
                        >
                          {{ index + 1 }}
                        </v-list-item-avatar>
                        <v-list-item-title>
                          {{ item.buttonLabel }}
                        </v-list-item-title>
                        <v-list-item-icon v-if="item.isReviewed">
                          <v-icon small>
                            mdi-check
                          </v-icon>
                        </v-list-item-icon>
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </div>
          <div
            v-if="!isLoading"
            class="mt-2 d-flex align-center"
          >
            <div class="w-50">
              <v-row align="center">
                <v-col
                  cols="auto"
                  class="my-1"
                >
                  <v-avatar
                    size="56"
                    color="blue-grey darken-5"
                  >
                    <img
                      v-if="partyHasPhoto"
                      :src="peGetPhotoDataUrl"
                    >
                    <span
                      v-else
                      class="white--text text-caption font-weight-bold"
                    >No<br>Photo</span>
                  </v-avatar>
                </v-col>
                <v-col>
                  <span>
                    <strong class="text-uppercase">
                      {{ partyFullName }}
                    </strong>

                    <div
                      class="text-caption d-block"
                    >
                      {{ peParty.gender_string }} | DOB:
                      {{ peParty.dob ? partyBirthdate : '-' }}
                      <strong
                        v-if="peParty.dob || peParty.uncertain_dob"
                      >
                        ({{ calculatedAge }})
                      </strong>
                      {{ aNumberFormatDisplay }}
                    </div>
                  </span>
                </v-col>
              </v-row>
            </div>
            <div class="w-50 text-h2">
              <v-carousel
                v-model="currentStepIndex"
                :continuous="false"
                height=""
                hide-delimiters
                :show-arrows="false"
                touchless
                vertical
              >
                <v-carousel-item
                  v-for="(step, index) of stepList"
                  :key="index"
                  class="text-right"
                >
                  {{ step.title }}
                </v-carousel-item>
              </v-carousel>
            </div>
          </div>
        </div>
        <v-divider />
        <div
          class="grow d-flex"
          style="position: relative;"
        >
          <div
            v-if="isLoading"
            class="grow d-flex align-center justify-center"
          >
            <v-progress-circular
              color="primary"
              indeterminate
              size="64"
            />
          </div>
          <v-tabs-items
            v-if="!isLoading"
            v-model="currentStepIndex"
            class="fill-height overflow-auto"
            style="position: absolute; inset: 0;"
          >
            <v-tab-item
              v-for="(step, index) of stepList"
              :key="index"
              style="min-height: 100%;"
            >
              <component
                :is="step.component"
                :error-list.sync="step.errorList"
                :instructions="step.instructions"
                @changed="_ => onStepChanged(step)"
              />
            </v-tab-item>
          </v-tabs-items>
          <v-overlay
            absolute
            opacity="0.05"
            :value="isSaving"
          />
        </div>
        <v-divider />
        <div class="mt-8 mb-4 d-flex">
          <div class="w-50 mx-4 d-flex align-center">
            <div
              v-if="!isLoading && totalStepCount"
              class="text-body-2"
            >
              Step {{ currentStepNumber }} of {{ totalStepCount }}

              <v-menu
                :disabled="isSaving"
                max-height="80vh"
                top
                offset-x
              >
                <template #activator="{ attrs, on }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-menu-down</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item-group
                    v-model="currentStepIndex"
                  >
                    <v-list-item
                      v-for="(item, index) of stepList.slice().reverse()"
                      :key="`step_list_${index}`"
                      :disabled="!item.isReviewed && (totalStepCount - index) !== unlockedStepCount"
                      :value="stepList.length - 1 - index"
                    >
                      <v-list-item-avatar
                        class="text-caption"
                        color="grey lighten-3"
                        size="24"
                      >
                        {{ stepList.length - index }}
                      </v-list-item-avatar>
                      <v-list-item-title>
                        {{ item.buttonLabel }}
                      </v-list-item-title>
                      <v-list-item-icon v-if="item.isReviewed">
                        <v-icon small>
                          mdi-check
                        </v-icon>
                      </v-list-item-icon>
                    </v-list-item>
                  </v-list-item-group>
                </v-list>
              </v-menu>

              <v-menu
                v-if="errorList.length"
                dark
                offset-y
                top
              >
                <template #activator="{ attrs, on }">
                  <v-chip
                    class="ml-8"
                    color="red darken-2"
                    dark
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-avatar
                      class="white"
                      left
                    >
                      <v-icon class="red--text darken-2">
                        mdi-exclamation
                      </v-icon>
                    </v-avatar>
                    {{ errorList.length }} errors found
                    <v-icon right>
                      mdi-menu-down
                    </v-icon>
                  </v-chip>
                </template>

                <v-list>
                  <template
                    v-for="(item, index) of errorList"
                  >
                    <v-divider
                      v-if="index"
                      :key="`divider_${index}`"
                    />
                    <v-list-item
                      :key="`list_item_${index}`"
                    >
                      <v-list-item-title>{{ item.$message }}</v-list-item-title>
                    </v-list-item>
                  </template>
                </v-list>
              </v-menu>
            </div>
            <v-spacer />
            <v-btn
              v-if="!isLoading"
              color="primary"
              :disabled="!canGoToPreviousStep || isSaving"
              text
              @click="_ => gotoPreviousStep()"
            >
              <v-icon left>
                mdi-chevron-left
              </v-icon>
              {{ previousStep.buttonLabel }}
            </v-btn>
          </div>
          <div class="w-50 mx-4 d-flex align-center">
            <v-btn
              v-if="!isLoading"
              ref="goToNextStepButton"
              color="primary"
              depressed
              :disabled="!canGoToNextStep"
              :loading="isSaving"
              @click="_ => gotoNextStep()"
            >
              {{ nextStep.buttonLabel }}
              <v-icon right>
                mdi-chevron-right
              </v-icon>
            </v-btn>
            <v-menu
              v-model="reviewDialog.isVisible"
              left
              :position-x="reviewDialog.x"
              :position-y="reviewDialog.y"
              top
            >
              <v-card>
                <v-card-text>
                  <p>You haven't made any changes on this step.</p>
                  <p>Please confirm that you have reviewed the existing data.</p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="primary"
                    depressed
                    @click="_ => gotoNextStep(true)"
                  >
                    Confirm
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
            <v-spacer />
            <v-btn
              v-if="!isLoading && isLastStep"
              color="primary"
              depressed
              :loading="isSaving"
              @click="_ => onFinish()"
            >
              <v-icon left>
                mdi-check-circle-outline
              </v-icon>
              Finish
            </v-btn>
            <v-menu
              v-else
              :disabled="isSaving"
              left
              offset-y
              top
            >
              <template #activator="{ attrs, on }">
                <v-btn
                  color="secondary"
                  plain
                  v-bind="attrs"
                  v-on="on"
                >
                  Close
                </v-btn>
              </template>
              <v-card>
                <v-card-text>
                  <p>Are you sure you want to cancel?</p>
                  <p>
                    You will not be able to resume this<br>
                    Quick Interview from the current step!
                  </p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="secondary"
                    depressed
                    @click="_ => onClose()"
                  >
                    Confirm
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </div>
        </div>
      </div>
    </v-sheet>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { cloneDeep } from 'lodash'
import { useVuelidate } from '@vuelidate/core'

import { aNumberFormatDisplay, calculatedAge, dateDisplayFormat } from '@/utilities'
import { alertHandler } from '@/alertHandler'
import { parseErrorResponse } from '@/utilities/error-responses'

import { PARTY_NAMESPACE } from '@/store/party'
import { QUICK_INTERVIEW_NAMESPACE } from '@/store/quick-interview'

import AddressesStep from '@/components/Party/QuickInterview/steps/AddressesStep.vue'
import AliasesStep from '@/components/Party/QuickInterview/steps/AliasesStep.vue'
import BackgroundOverviewStep from '@/components/Party/QuickInterview/steps/BackgroundOverviewStep.vue'
import ChildrenStep from '@/components/Party/QuickInterview/steps/ChildrenStep.vue'
import ContactsStep from '@/components/Party/QuickInterview/steps/ContactsStep.vue'
import DemographicsStep from '@/components/Party/QuickInterview/steps/DemographicsStep.vue'
import FamilyOverviewStep from '@/components/Party/QuickInterview/steps/FamilyOverviewStep.vue'
import HistoryOverviewStep from '@/components/Party/QuickInterview/steps/HistoryOverviewStep.vue'
import IdentifiersStep from '@/components/Party/QuickInterview/steps/IdentifiersStep.vue'
import ImmigrationBenefitsStep from '@/components/Party/QuickInterview/steps/ImmigrationBenefitsStep.vue'
import ImmigrationProceedingsStep from '@/components/Party/QuickInterview/steps/ImmigrationProceedingsStep.vue'
import ImmigrationStatusStep from '@/components/Party/QuickInterview/steps/ImmigrationStatusStep.vue'
import IncomeStep from '@/components/Party/QuickInterview/steps/IncomeStep.vue'
import InterviewSummaryStep from '@/components/Party/QuickInterview/steps/InterviewSummaryStep.vue'
import IntroductionStep from '@/components/Party/QuickInterview/steps/IntroductionStep.vue'
import LanguagesStep from '@/components/Party/QuickInterview/steps/LanguagesStep.vue'
import MarriagesStep from '@/components/Party/QuickInterview/steps/MarriagesStep.vue'
import NarrativesStep from '@/components/Party/QuickInterview/steps/NarrativesStep.vue'
import ParentsStep from '@/components/Party/QuickInterview/steps/ParentsStep.vue'
import PartyDetailsStep from '@/components/Party/QuickInterview/steps/PartyDetailsStep.vue'
import PassportsStep from '@/components/Party/QuickInterview/steps/PassportsStep.vue'
import PersonalOverviewStep from '@/components/Party/QuickInterview/steps/PersonalOverviewStep.vue'
import SiblingsStep from '@/components/Party/QuickInterview/steps/SiblingsStep.vue'
import SuggestLegalCasesStep from '@/components/Party/QuickInterview/steps/SuggestLegalCasesStep.vue'
import TravelHistoriesStep from '@/components/Party/QuickInterview/steps/TravelHistoriesStep.vue'

export default {
  name: 'QuickInterviewWizard',

  components: {
    AddressesStep,
    AliasesStep,
    BackgroundOverviewStep,
    ChildrenStep,
    ContactsStep,
    DemographicsStep,
    FamilyOverviewStep,
    HistoryOverviewStep,
    IdentifiersStep,
    ImmigrationBenefitsStep,
    ImmigrationProceedingsStep,
    ImmigrationStatusStep,
    IncomeStep,
    InterviewSummaryStep,
    IntroductionStep,
    LanguagesStep,
    MarriagesStep,
    NarrativesStep,
    ParentsStep,
    PartyDetailsStep,
    PassportsStep,
    PersonalOverviewStep,
    SiblingsStep,
    SuggestLegalCasesStep,
    TravelHistoriesStep,
  },

  model: {
    prop: 'visible',
    event: 'change',
  },

  props: {
    interviewId: {
      type: Number,
      default: null,
    },

    visible: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      ...alertHandler(),
      v$: useVuelidate(),
    }
  },

  data () {
    return {
      intervalId: null,
      isLoading: true,
      isSaving: false,
      reviewDialog: {
        isVisible: false,
        x: null,
        y: null,
      },
    }
  },

  computed: {
    aNumberFormatDisplay () {
      return this.peParty.a_number ? `| A# ${aNumberFormatDisplay(this.peParty.a_number)}` : ''
    },

    calculatedAge () {
      return calculatedAge(this.peParty.dob, this.peParty.uncertain_dob)
    },

    canGoToNextStep () {
      return this.stepList.length && !this.isLastStep && !this.inEditMode && !this.hasErrors
    },

    canGoToPreviousStep () {
      return !this.isFirstStep && !this.inEditMode
    },

    currentStep () {
      return this.stepList[this.currentStepIndex] || {}
    },

    currentStepIndex: {
      get () { return this.quickInterview.last_step_index },

      set (value) {
        this.quickInterview.last_step_title = this.stepList[value].title
        this.quickInterview.last_step_index = value
      },
    },

    currentStepNumber () {
      return this.totalStepCount ? this.currentStepIndex + 1 : 0
    },

    errorList () {
      return this.currentStep?.errorList || []
    },

    hasErrors () {
      return !!this.errorList.length
    },

    inEditMode () {
      return this.peAnyUnlockedSections
    },

    isFirstStep () {
      return !(this.currentStepNumber > 1)
    },

    isLastStep () {
      return !this.totalStepCount || this.currentStepNumber === this.totalStepCount
    },

    isPendingReview () {
      return this.currentStep.requiresReview && !this.currentStep.isReviewed
    },

    isVisible: {
      get () { return this.visible },
      set (value) { this.$emit('change', value) },
    },

    nextStep () {
      return this.stepList[this.currentStepIndex + 1] || { buttonLabel: 'Next' }
    },

    partyBirthdate () {
      return dateDisplayFormat(this.peParty.dob)
    },

    partyFullName () {
      const headerString = [this.peParty.first_name]
      if (this.peParty.middle_name) headerString.push(this.peParty.middle_name)
      headerString.push(this.peParty.last_name)
      headerString.push(`(ID: ${this.peParty.id})`)

      return headerString.join(' ')
    },

    partyHasPhoto () {
      return !!this.peGetPhotoDataUrl
    },

    previousStep () {
      if (this.isFirstStep) return { buttonLabel: 'Previous' }
      return this.stepList[this.currentStepIndex - 1] || { buttonLabel: 'Previous' }
    },

    stepList () {
      return this.quickInterview.steps_data
    },

    totalStepCount () {
      return this.stepList?.length || 0
    },

    unlockedStepCount () {
      return this.stepList.reduce((res, step, index) => {
        if (step.isReviewed) return index + 1
        return res
      }, 0) + 1
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peAnyUnlockedSections',
      'peGetPhotoDataUrl',
      'peParty',
    ]),

    ...mapGetters(QUICK_INTERVIEW_NAMESPACE, [
      'quickInterview',
    ]),
  },

  watch: {
    visible: {
      handler: function (value) {
        if (value) this.initializeQuickInterview()
      },

      immediate: true,
    },
  },

  beforeDestroy () {
    if (!this.intervalId) return

    clearInterval(this.intervalId)
    this.intervalId = null
  },

  methods: {
    async gotoNextStep (reviewed) {
      await this.v$.$validate()
      if (this.errorList?.length) return

      if (!this.currentStep.hasChanged && this.currentStep.requiresReview && !this.currentStep.isReviewed && !reviewed) {
        const activator = this.$refs.goToNextStepButton.$el
        this.reviewDialog.x = activator.offsetLeft + activator.offsetWidth
        this.reviewDialog.y = activator.offsetTop
        this.reviewDialog.isVisible = true

        return
      }

      if (!this.isLastStep && this.currentStep.isReviewed) {
        this.currentStepIndex++
        return
      }

      this.isSaving = true

      this.updateQuickInterview(cloneDeep(this.peParty)).
        then(_ => {
          this.currentStep.isReviewed = true
          this.currentStepIndex++
        }).
        catch(error => this.displayAlert('error', parseErrorResponse(error))).
        finally(_ => { this.isSaving = false })
    },

    gotoPreviousStep () {
      if (this.currentStepIndex > 0) this.currentStepIndex--
    },

    async initializeQuickInterview () {
      this.isLoading = true
      this.isSaving = false

      await this.loadPartyForQuickInterview({ id: this.peParty.id })
      const partyData = cloneDeep(this.peParty)

      this.getQuickInterviewsSteps().
        then(_ => {
          return (this.interviewId
            ? this.resumeQuickInterview(this.interviewId)
            : this.createQuickInterview({ partyData: partyData, partyId: partyData.id })
          ).
            then(_ => {
              if (this.interviewId) {
                this.currentStep.isReviewed = true
                this.currentStepIndex++
              }

              if (!this.intervalId) { this.intervalId = setInterval(_ => { this.resetQuickInterviewTimeout() }, 30000) }
              this.isLoading = false
            })
        }).
        catch(error => {
          this.displayAlert('error', parseErrorResponse(error))
          this.isVisible = false
        })
    },

    onClose () {
      this.isSaving = true

      this.closeQuickInterview(this.quickInterview.id).
        then(_ => {
          if (this.peAnyUnlockedSections) this.peLockPartySections()
          this.isVisible = false
        }).
        catch(error => this.displayAlert('error', parseErrorResponse(error))).
        finally(_ => { this.isSaving = false })
    },

    async onFinish () {
      await this.v$.$validate()
      if (this.errorList?.length) return

      this.isSaving = true

      this.finishQuickInterview(cloneDeep(this.peParty)).
        then(_ => {
          this.displayAlert('success', 'Quick Interview successfully finished')
          this.isVisible = false
        }).
        catch(error => this.displayAlert('error', parseErrorResponse(error))).
        finally(_ => { this.isSaving = false })
    },

    onStepChanged (step) {
      step.hasChanged = true
    },

    ...mapActions(PARTY_NAMESPACE, [
      'loadPartyForQuickInterview',
      'peLockPartySections',
    ]),

    ...mapActions(QUICK_INTERVIEW_NAMESPACE, [
      'closeQuickInterview',
      'createQuickInterview',
      'finishQuickInterview',
      'getQuickInterviewsSteps',
      'resetQuickInterviewTimeout',
      'resumeQuickInterview',
      'updateQuickInterview',
    ]),
  },
}
</script>
